import { serviceDocketInventoryCheckStatus } from '../vending-settings';
import clamp from '../math/clamp';
import {
	normalizeDateKey,
	serializeDateKeyForTimezone,
} from '../warehousing/formatExpiryBatchSummary';

export const RESTOCK_CORRECTION_INVENTORY_CHECK_CHANGED_ERROR =
	'Stock confirmation changed after this correction opened. Refresh and review the latest quantities.';
export const RESTOCK_CORRECTION_REVIEW_REFRESHED_MESSAGE =
	'Stock confirmation changed while this correction was open. ' +
	'Review the updated Missing and Extra amounts before confirming.';

export const getRestockExpiryValues = expiryDates => Object.keys(expiryDates || {})
	.map(key => Number.parseInt(key, 10))
	.filter(key => Number.isInteger(key) && key > 0)
	.sort((left, right) => left - right)
	.map(key => expiryDates[key] || null);

export const getRestockBatchDraft = (expiryDates, timezone) => {
	const expiryValues = getRestockExpiryValues(expiryDates);
	const dateKeys = [...new Set(expiryValues
		.map(value => normalizeDateKey(value, timezone))
		.filter(Boolean))];
	const hasUnknownBatch = expiryValues.some(value => !normalizeDateKey(value, timezone));
	const isMixedBatch = dateKeys.length > 1 || (dateKeys.length > 0 && hasUnknownBatch);

	return {
		dateKey: dateKeys.length === 1 && !hasUnknownBatch ? dateKeys[0] : '',
		label: isMixedBatch
			? 'Mixed or partly unknown batches'
			: dateKeys[0] || (expiryValues.length ? 'Unknown batch' : 'No batch recorded'),
		isMixedBatch,
		hasUnknownBatch,
	};
};

export const isRestockInventoryConfirmed = inventoryCheck => [
	serviceDocketInventoryCheckStatus.CONFIRMED,
	serviceDocketInventoryCheckStatus.CONFIRMED_WITH_CHANGES,
].includes(inventoryCheck);

export const toRestockCorrectionIdString = value => String(value?.id || value?._id || value || '');

export const didRestockBatchChange = (before, after) => JSON.stringify(before?.planogramItemExpiryDatesAtPick || {}) !== JSON.stringify(after?.planogramItemExpiryDatesAtPick || {});

const clampRestockVariance = (quantity, parlevel) => {
	const maximum = Number(parlevel) ? Number(parlevel) : Math.abs(quantity);
	return clamp(quantity, 0, maximum);
};

export const getConfirmedRestockCorrectionPreview = ({
	items = [],
	changes = {},
}) => {
	let invalidMessage = null;
	const previewItems = [];

	items.forEach(item => {
		const change = changes[item.id];
		const currentDeliveredQuantity = Number(item.restockDeliveredQuantity || 0);
		const correctedDeliveredQuantity = Number(change?.deliveredQuantity);
		if (
			!change ||
			!Number.isFinite(correctedDeliveredQuantity) ||
			correctedDeliveredQuantity === currentDeliveredQuantity
		) {
			return;
		}

		const confirmedQuantityValue = item.planogramItemQuantityPostInventoryChange;
		if (
			confirmedQuantityValue === undefined ||
			confirmedQuantityValue === null ||
			!Number.isFinite(Number(confirmedQuantityValue))
		) {
			invalidMessage = `The confirmed stock snapshot for ${item.product?.name || 'a product'} is unavailable.`;
			return;
		}

		const currentExpectedQuantity = Number(item.planogramItemQuantityPostService || 0);
		const correctedExpectedQuantity = currentExpectedQuantity +
			correctedDeliveredQuantity - currentDeliveredQuantity;
		if (correctedExpectedQuantity < 0) {
			invalidMessage = `The corrected expected stock for ${item.product?.name || 'a product'} cannot be below zero.`;
			return;
		}

		const confirmedQuantity = Number(confirmedQuantityValue);
		const hasSeparateRemovedProduct = Boolean(toRestockCorrectionIdString(item.removedProduct));
		const spoiledQuantity = hasSeparateRemovedProduct
			? 0
			: Math.max(0, Number(item.spoiledProductQuantity || 0));
		const removedQuantity = hasSeparateRemovedProduct
			? 0
			: Math.max(0, Number(item.removedProductQuantity || 0));
		const missingQuantity = correctedExpectedQuantity - confirmedQuantity -
			spoiledQuantity - removedQuantity;
		const extraQuantity = confirmedQuantity - correctedExpectedQuantity -
			spoiledQuantity - removedQuantity;
		const hasSavedClassifications = Boolean((item.stockReductionClassifications || []).length);
		const nextMissingQuantity = hasSavedClassifications
			? Math.max(0, missingQuantity)
			: clampRestockVariance(missingQuantity, item.planogramItemParlevel);
		const nextExtraQuantity = clampRestockVariance(extraQuantity, item.planogramItemParlevel);
		const currentMissingQuantity = Math.max(0, Number(item.missingProductQuantity || 0));
		const currentExtraQuantity = Math.max(0, Number(item.extraProductQuantity || 0));
		// Correction availability excludes active Not picked units, so the remaining
		// saved reduction reasons must still equal the corrected physical reduction.
		if (
			hasSavedClassifications &&
			nextMissingQuantity + spoiledQuantity + removedQuantity !==
				Math.max(0, correctedExpectedQuantity - confirmedQuantity)
		) {
			invalidMessage = `Review the saved stock reduction reasons for ${item.product?.name || 'a product'} before correcting its delivered quantity.`;
			return;
		}

		previewItems.push({
			item,
			correctedDeliveredQuantity,
			correctedExpectedQuantity,
			confirmedQuantity,
			missing: {
				before: currentMissingQuantity,
				after: nextMissingQuantity,
				change: nextMissingQuantity - currentMissingQuantity,
			},
			extra: {
				before: currentExtraQuantity,
				after: nextExtraQuantity,
				change: nextExtraQuantity - currentExtraQuantity,
			},
		});
	});

	const totals = previewItems.reduce((result, previewItem) => ({
		missing: {
			before: result.missing.before + previewItem.missing.before,
			after: result.missing.after + previewItem.missing.after,
			change: result.missing.change + previewItem.missing.change,
		},
		extra: {
			before: result.extra.before + previewItem.extra.before,
			after: result.extra.after + previewItem.extra.after,
			change: result.extra.change + previewItem.extra.change,
		},
	}), {
		missing: { before: 0, after: 0, change: 0 },
		extra: { before: 0, after: 0, change: 0 },
	});

	return {
		items: previewItems,
		totals,
		invalidMessage,
	};
};

export const validateMarketRestockCorrection = ({
	items,
	draftById,
	timezone,
}) => {
	const changes = {};
	let hasQuantityChange = false;
	let invalidMessage = null;

	items.forEach(item => {
		const draft = draftById[item.id];
		if (!draft) {
			return;
		}
		if (draft.quantity === '' || draft.quantity === null || draft.quantity === undefined) {
			invalidMessage = 'Enter a corrected delivered quantity for every product.';
			return;
		}
		const deliveredQuantity = Number(draft.quantity);
		const currentQuantity = Number(item.restockDeliveredQuantity || 0);
		const swapQuantity = Math.max(0, Number(item.swapQuantity || 0));
		if (!Number.isInteger(deliveredQuantity) || deliveredQuantity < 0) {
			invalidMessage = 'Corrected delivered quantities must be whole numbers of zero or greater.';
			return;
		}
		if (deliveredQuantity < swapQuantity) {
			invalidMessage = `${item.product?.name || 'A product'} cannot be lower than its swapped quantity of ${swapQuantity}.`;
			return;
		}

		const quantityChanged = deliveredQuantity !== currentQuantity;
		if (quantityChanged) {
			hasQuantityChange = true;
		}
		const hasBatchProvenance = Boolean(item.product?.isFreshFood ||
			getRestockExpiryValues(item.planogramItemExpiryDatesAtPick).length);
		const quantityIncreased = deliveredQuantity > currentQuantity;
		if (
			hasBatchProvenance &&
			quantityChanged &&
			!draft.expiryDate &&
			(draft.isMixedBatch || quantityIncreased)
		) {
			invalidMessage = `${item.product?.name || 'A product'} needs one authoritative delivered batch expiry for this correction.`;
			return;
		}
		const shouldSubmitExpiryDates = draft.expiryDateChanged || (
			hasBatchProvenance && quantityChanged && Boolean(draft.expiryDate)
		);
		if (quantityChanged || draft.expiryDateChanged) {
			changes[item.id] = {
				deliveredQuantity,
				...(shouldSubmitExpiryDates ? {
					expiryDates: {
						1: serializeDateKeyForTimezone(draft.expiryDate, timezone),
					},
				} : {}),
			};
		}
	});

	return {
		changes,
		hasQuantityChange,
		invalidMessage,
	};
};
